import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Download,
  Refresh,
  CheckCircle,
  Cancel,
  Warning,
  Error,
  Description,
  Send,
} from '@mui/icons-material';
import ProgressIndicator from './ProgressIndicator';
import DiagnosticCard, { DiagnosticStatus } from './DiagnosticCard';

interface DiagnosticResult {
  title: string;
  status: DiagnosticStatus;
  message: string;
  data?: any;
}

interface MessageTestResult {
  status: 'success' | 'failed';
  messageId?: string;
  deliveryTime?: string;
  carrier?: string;
  timestamp?: string;
  error?: string;
  skipped?: boolean;
}

interface CompleteDiagnosticReportProps {
  phoneNumber: string;
  userEmail: string;
  ticketId: string;
  diagnosticResults: DiagnosticResult[];
  messageTestResult?: MessageTestResult;
  onTestAnotherNumber: () => void;
  onDownloadReport: () => void;
}

const CompleteDiagnosticReport: React.FC<CompleteDiagnosticReportProps> = ({
  phoneNumber,
  userEmail,
  ticketId,
  diagnosticResults,
  messageTestResult,
  onTestAnotherNumber,
  onDownloadReport,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const getSummaryStats = () => {
    const passed = diagnosticResults.filter(r => r.status === 'success').length;
    const warnings = diagnosticResults.filter(r => r.status === 'warning').length;
    const errors = diagnosticResults.filter(r => r.status === 'failed' || r.status === 'error').length;
    
    return { passed, warnings, errors };
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      await onDownloadReport();
    } finally {
      setIsDownloading(false);
    }
  };

  const stats = getSummaryStats();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: { xs: 2, md: 4 },
      }}
    >
      <ProgressIndicator currentStep={4} steps={['Input', 'Diagnostics', 'Results', 'Report']} />
      
      <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header with Icon */}
        <Box sx={{ textAlign: 'center', mb: 6, mt: -4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                backgroundColor: '#66BB6A',
                boxShadow: '0 8px 24px rgba(102, 187, 106, 0.3)',
              }}
            >
              <Description sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
          </Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Complete Diagnostic Report
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              mb: 1,
              opacity: 0.9,
              fontWeight: 500,
            }}
          >
            Comprehensive analysis and test results
          </Typography>
          <Chip
            label={`Ticket: ${ticketId}`}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          />
          
          {/* User Information */}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              User Email: {userEmail || 'Not provided'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              Phone Number: {phoneNumber}
            </Typography>
          </Box>
        </Box>

        {/* Summary Stats */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={3}>
            <Card 
              sx={{ 
                backgroundColor: '#4caf50', 
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(76, 175, 80, 0.4)',
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                  {stats.passed}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Passed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card 
              sx={{ 
                backgroundColor: '#ff9800', 
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(255, 152, 0, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(255, 152, 0, 0.4)',
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                  {stats.warnings}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Warnings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card 
              sx={{ 
                backgroundColor: '#f44336', 
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(244, 67, 54, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(244, 67, 54, 0.4)',
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                  {stats.errors}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Errors
                </Typography>
              </CardContent>
            </Card>
          </Grid>
                     <Grid item xs={12} md={3}>
             <Card 
               sx={{ 
                 backgroundColor: messageTestResult?.status === 'success' ? '#4caf50' : '#2196f3', 
                 color: 'white',
                 borderRadius: 3,
                 boxShadow: messageTestResult?.status === 'success' 
                   ? '0 8px 24px rgba(76, 175, 80, 0.3)'
                   : '0 8px 24px rgba(33, 150, 243, 0.3)',
                 transition: 'all 0.3s ease',
                 '&:hover': {
                   transform: 'translateY(-4px)',
                   boxShadow: messageTestResult?.status === 'success'
                     ? '0 12px 32px rgba(76, 175, 80, 0.4)'
                     : '0 12px 32px rgba(33, 150, 243, 0.4)',
                 },
               }}
             >
               <CardContent sx={{ textAlign: 'center', p: 4 }}>
                 <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                   {messageTestResult?.status === 'success' ? '✓' : 'X'}
                 </Typography>
                 <Typography variant="h6" sx={{ fontWeight: 600 }}>
                   Text Message
                 </Typography>
               </CardContent>
             </Card>
           </Grid>
        </Grid>

                 {/* Message Test Results */}
         {messageTestResult && (
          <Paper
            sx={{
              p: 4,
              mb: 6,
              backgroundColor: messageTestResult.skipped 
                ? 'rgba(255, 152, 0, 0.1)'  // Orange for skipped
                : messageTestResult.status === 'success' 
                  ? 'rgba(76, 175, 80, 0.1)' 
                  : '#fcdcdc',
              borderRadius: 3,
              border: '2px solid',
              borderColor: messageTestResult.skipped 
                ? '#ff9800'  // Orange for skipped
                : messageTestResult.status === 'success' ? '#4caf50' : '#f44336',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: messageTestResult.skipped 
                    ? '#ff9800'  // Orange for skipped
                    : messageTestResult.status === 'success' ? '#4caf50' : '#f44336',
                  color: 'white',
                  mr: 2,
                }}
              >
                <Send />
              </Avatar>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: messageTestResult.skipped 
                    ? '#ff9800'  // Orange for skipped
                    : messageTestResult.status === 'success' ? '#4caf50' : '#f44336',
                }}
              >
                {messageTestResult.skipped ? 'Number Setup Check' : 'Live Message Test Results'}
              </Typography>
            </Box>
           
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    <strong>Status:</strong> {messageTestResult.skipped ? 'SETUP REQUIRED' : messageTestResult.status.toUpperCase()}
                  </Typography>
                  {messageTestResult.messageId && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Message ID:</strong> {messageTestResult.messageId}
                    </Typography>
                  )}
                  {messageTestResult.error && (
                    <Typography variant="body1" sx={{ mb: 1, color: messageTestResult.skipped ? '#ff9800' : '#f44336', fontWeight: 600 }}>
                      <strong>{messageTestResult.skipped ? 'Note:' : 'Error:'}</strong> {messageTestResult.error}
                    </Typography>
                  )}
                  {messageTestResult.skipped && (
                    <Typography variant="body1" sx={{ mb: 1, color: '#666', fontStyle: 'italic' }}>
                      Your phone number needs to be configured in our system before test messages can be sent.
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  {messageTestResult.deliveryTime && !messageTestResult.skipped && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Delivery Time:</strong> {messageTestResult.deliveryTime}
                    </Typography>
                  )}
                  {/* Commented out - not showing carrier
		  {messageTestResult.carrier && !messageTestResult.skipped && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Carrier:</strong> {messageTestResult.carrier}
                    </Typography>
                  )}
		  */}
                  {messageTestResult.timestamp && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Timestamp:</strong> {messageTestResult.timestamp}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Detailed Diagnostic Results */}
        <Paper 
          sx={{ 
            p: 4, 
            mb: 6, 
            backgroundColor: 'white', 
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 4,
              color: 'text.primary',
            }}
          >
            Detailed Diagnostic Results
          </Typography>
                     <Box>
             {diagnosticResults.map((result, index) => (
               <Box key={index} sx={{ mb: 3 }}>
                 <DiagnosticCard
                   title={result.title}
                   status={result.status}
                   message={result.message}
                   data={result.data}
                 />
               </Box>
             ))}
           </Box>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={onTestAnotherNumber}
            sx={{
              borderRadius: 3,
              py: 2,
              px: 4,
              fontWeight: 600,
              fontSize: '1rem',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.2)',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Test Another Number
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadReport}
            disabled={isDownloading}
            sx={{
              borderRadius: 3,
              py: 2,
              px: 4,
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              backgroundColor: '#424242',
              boxShadow: '0 4px 12px rgba(66, 66, 66, 0.3)',
              '&:hover': {
                backgroundColor: '#616161',
                boxShadow: '0 6px 16px rgba(66, 66, 66, 0.4)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            {isDownloading ? 'Downloading...' : 'Download Report'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CompleteDiagnosticReport; 
